/* eslint-disable no-console */
import { takeLatest, call, all, put, select } from 'redux-saga/effects';

// Services
import { tripsApi } from 'services/apiDigitalTicket/trips';

// Helpers
import { getLineRoute } from 'helpers/lines';
import { getTripRoute } from 'helpers/trips';
import { isObject } from 'helpers/isObject';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import { CONST_LINES_OBJECT } from 'constants/lines';

import * as TripsActions from './actions';
import { TripInterface, TripTypes } from './types';

function* listDetail({
  payload,
}: ReturnType<typeof TripsActions.listTripsDetailRequest>): any {
  try {
    const { params, recovers, showLoading } = payload;

    if (showLoading) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando viagens' }));
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...params },
    });

    if (response.status === 200 && Array.isArray(response.data)) {
      let trips = response.data;
      const recoverLineRoute = recovers && recovers.includes('lineroute');
      const recoverTripRoute = recovers && recovers.includes('triproute');

      if (recoverLineRoute) {
        const lineRoutesRes = yield all(
          trips.map((trip: TripInterface) => {
            const geojsonUrl =
              trip.LineSection?.way === CONST_LINES_OBJECT.WAYS.GO.KEY
                ? trip.LineSection?.Line.goRoute
                : trip.LineSection?.Line.backRoute;
            if (geojsonUrl) {
              return call(() => getLineRoute(geojsonUrl));
            }
            return null;
          }),
        );

        if (Array.isArray(lineRoutesRes)) {
          trips = trips.map((t: any, i: number) => ({
            ...t,
            LineRoute: lineRoutesRes[i],
          }));
        }
      }

      if (recoverTripRoute) {
        const tripRoutesRes = yield all(
          trips.map(({ tripDataUrl }: TripInterface) => {
            if (tripDataUrl) {
              return call(() => getTripRoute(tripDataUrl));
            }
            return null;
          }),
        );

        if (Array.isArray(tripRoutesRes)) {
          trips = trips.map((t: any, i: number) => ({
            ...t,
            TripRoute: tripRoutesRes[i],
          }));
        }
      }

      if (showLoading) {
        yield put(DialogsActions.setClose());
      }

      yield put(
        TripsActions.listTripsDetailSuccess({
          tripDetailsList: trips,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('trips - listTrips - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.listTripsDetailFailure());
    yield put(DialogsActions.setClose());
  }
}

function* list({
  payload,
}: ReturnType<typeof TripsActions.listTripsRequest>): any {
  try {
    const { params, recovers, showLoading, isCsvFull } = payload;

    if (showLoading) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando viagens' }));
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const baseParams = {
      transportOperatorId: transportOperator.id,
      ...params,
    };

    const response = yield call(tripsApi.get, {
      params: baseParams,
    });

    if (response.status === 200) {
      let trips: any[] | null = null;

      if (Array.isArray(response.data)) {
        trips = response.data;
      }

      if (isObject(response.data)) {
        trips = [response.data];
      }

      if (!trips) return;

      const recoverLineRoute = recovers && recovers.includes('lineroute');
      const recoverTripRoute = recovers && recovers.includes('triproute');

      if (recoverLineRoute) {
        const lineRoutesRes = yield all(
          trips.map((trip: TripInterface) => {
            const geojsonUrl =
              trip.LineSection?.way === CONST_LINES_OBJECT.WAYS.GO.KEY
                ? trip.LineSection?.Line.goRoute
                : trip.LineSection?.Line.backRoute;
            if (geojsonUrl) {
              return call(() => getLineRoute(geojsonUrl));
            }
            return null;
          }),
        );

        if (Array.isArray(lineRoutesRes)) {
          trips = trips.map((t: any, i: number) => ({
            ...t,
            LineRoute: lineRoutesRes[i],
          }));
        }
      }

      if (recoverTripRoute) {
        const tripRoutesRes = yield all(
          trips.map(({ tripDataUrl }: TripInterface) => {
            if (tripDataUrl) {
              return call(() => getTripRoute(tripDataUrl));
            }
            return null;
          }),
        );

        if (Array.isArray(tripRoutesRes)) {
          trips = trips.map((t: any, i: number) => ({
            ...t,
            TripRoute: tripRoutesRes[i],
          }));
        }
      }

      const mappedTrips = trips?.map((trip) => ({
        ...trip,
        prefix: trip?.Car?.prefix || '',
        license: trip?.Car?.license || '',
        line: trip?.LineSection?.Line?.name || '',
        employee: trip?.TransportEmployee?.name || '',
      }));

      yield put(
        TripsActions.listTripsSuccess({
          tripList: mappedTrips,
          loading: !!isCsvFull,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      if (isCsvFull) {
        const responseFull = yield call(tripsApi.get, {
          params: {
            ...baseParams,
            _limit: 99999,
          },
        });

        if (responseFull.status === 200) {
          let tripsFull: any[] | null = null;

          if (Array.isArray(responseFull.data)) {
            tripsFull = responseFull.data;
          }

          if (isObject(responseFull.data)) {
            tripsFull = [responseFull.data];
          }

          if (tripsFull) {
            const mappedTripsFull = tripsFull.map((trip) => ({
              ...trip,
              prefix: trip?.Car?.prefix || '',
              license: trip?.Car?.license || '',
              line: trip?.LineSection?.Line?.name || '',
              employee: trip?.TransportEmployee?.name || '',
            }));

            yield put(
              TripsActions.listTripsSuccess({
                tripList: mappedTripsFull,
                isCsvFull: true,
                loading: false,
                totalCount: parseInt(responseFull.headers.totalcount, 10),
              }),
            );
          }
        }
      }
      if (showLoading) {
        yield put(DialogsActions.setClose());
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('trips - listTrips - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.listTripsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
  meta,
}: ReturnType<typeof TripsActions.getTripsRequest>): any {
  try {
    const { params, recovers } = payload as { params: object; recovers: any };

    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(tripsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...params },
    });

    if (response.status === 200) {
      let trips: any[] | null = null;

      if (isObject(response.data)) {
        trips = [response.data];
      }

      if (!trips) return;

      const recoverLineRoute = recovers && recovers.includes('lineroute');
      const recoverTripRoute = recovers && recovers.includes('triproute');

      if (recoverLineRoute) {
        const lineRoutesRes = yield all(
          trips.map((trip: TripInterface) => {
            const geojsonUrl =
              trip.LineSection?.way === CONST_LINES_OBJECT.WAYS.GO.KEY
                ? trip.LineSection?.Line.goRoute
                : trip.LineSection?.Line.backRoute;
            if (geojsonUrl) {
              return call(() => getLineRoute(geojsonUrl));
            }
            return null;
          }),
        );

        if (Array.isArray(lineRoutesRes)) {
          trips = trips.map((t: any, i: number) => ({
            ...t,
            LineRoute: lineRoutesRes[i],
          }));
        }
      }

      if (recoverTripRoute) {
        const tripRoutesRes = yield all(
          trips.map(({ tripDataUrl }: TripInterface) => {
            if (tripDataUrl) {
              return call(() => getTripRoute(tripDataUrl));
            }
            return null;
          }),
        );

        if (Array.isArray(tripRoutesRes)) {
          trips = trips.map((t: any, i: number) => ({
            ...t,
            TripRoute: tripRoutesRes[i],
          }));
        }
      }

      yield put(
        TripsActions.getTripsSuccess({
          tripData: trips[0],
        }),
      );

      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('trips - listTrips - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.getTripsFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* download({
  payload,
}: ReturnType<typeof TripsActions.tripsPDFRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Gerando o PDF' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.pdfDownload, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TripsActions.tripsPDFSuccess());

      yield put(DialogsActions.setClose());

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' }),
      );
      window.open(url, '_blank');
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('trips - listTrips - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.tripsPDFFailure());
    yield put(DialogsActions.setClose());
  }
}

function* payment({
  payload,
}: ReturnType<typeof TripsActions.tripsPaymentRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Adicionando pagamento' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.addPayment, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TripsActions.tripsPaymentSuccess());

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('trips - tripsAddPayment - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.tripsPaymentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* approval({
  payload,
}: ReturnType<typeof TripsActions.tripsApprovalRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Verificando status da viagem',
      }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.addApproval, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TripsActions.tripsApprovalSuccess());

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('trips - tripsAddApproval - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.tripsApprovalFailure());
    yield put(DialogsActions.setClose());
  }
}

function* approvalAll({
  payload,
}: ReturnType<typeof TripsActions.tripsApprovalAllRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Aprovando todas as viagens',
      }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.addApprovalAll, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TripsActions.tripsApprovalAllSuccess());

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('trips - tripsAddApprovalAll - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.tripsApprovalAllFailure());
    yield put(DialogsActions.setClose());
  }
}

function* reportHtml({
  payload,
}: ReturnType<typeof TripsActions.tripReportHtmlRequest>): any {
  try {
    // yield put(
    //   DialogsActions.setOpenLoading({
    //     title: 'Listando dados da tabela',
    //   }),
    // );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.reportHtml, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TripsActions.tripReportHtmlSuccess({
          tripReportHtml: response.data,
        }),
      );

      // yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('trips - tripReportHtml - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.tripReportHtmlFailure());
    yield put(DialogsActions.setClose());
  }
}

function* tripReport({
  payload,
}: ReturnType<typeof TripsActions.tripReportRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando dados',
      }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.tripReport, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TripsActions.tripReportSuccess({
          tripReport: response.data,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('trips - tripReport - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.tripReportFailure());
    yield put(DialogsActions.setClose());
  }
}

function* tripUpdateControl({
  payload,
}: ReturnType<typeof TripsActions.tripUpdateControlRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando catraca' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.tripUpdateControl, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TripsActions.tripUpdateControlSuccess({
          tripReportControl: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Funcionário alterado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - updateEmployee - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.tripUpdateControlFailure());
    yield put(DialogsActions.setClose());
  }
}

function* tripControlSettingsGet({
  payload,
}: ReturnType<typeof TripsActions.tripControlSettingsGetRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.getControlSettings, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TripsActions.tripControlSettingsGetSuccess({
          tripControlSettingsData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - tripControlSettingsGet - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.tripControlSettingsGetFailure());
    yield put(DialogsActions.setClose());
  }
}

function* tripControlSettingsUpdate({
  payload,
}: ReturnType<typeof TripsActions.tripControlSettingsUpdateRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando catraca' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.tripControlSettingsUpdate, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TripsActions.tripControlSettingsUpdateSuccess({
          tripControlSettingsData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Funcionario salvo com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employees - updateEmployee - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.tripControlSettingsUpdateFailure());
    yield put(DialogsActions.setClose());
  }
}

function* monitoringSettingsList({
  payload,
}: ReturnType<typeof TripsActions.monitoringSettingsListRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando configurações' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.monitoringKmSettingsGet, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TripsActions.monitoringSettingsListSuccess({
          monitoringSettingsList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('monitoringSettingsList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.monitoringSettingsListFailure());
    yield put(DialogsActions.setClose());
  }
}

function* monitoringSettingsUpdate({
  payload,
}: ReturnType<typeof TripsActions.monitoringSettingsUpdateRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Editando configurações' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.monitoringKmSettingsUpdate, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TripsActions.monitoringSettingsUpdateSuccess({
          monitoringSettingsList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Configurações salvo com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('monitoringSettingsList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TripsActions.monitoringSettingsUpdateFailure());
    yield put(DialogsActions.setClose());
  }
}

function* tripAskMissingEntries({
  payload,
}: ReturnType<typeof TripsActions.tripAskMissingEntriesRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Atualilando  Lista' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(tripsApi.tripAskMissingEntries, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TripsActions.getTripsRequest({
          params: {
            id: payload.id,
            _includes: 'occurrence,metric,linestop,car,control',
          },
          recovers: 'lineroute,triproute',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Lista Atualisada com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('tripAskMissingEntries - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const tripsSagas = [
  takeLatest(TripTypes.LIST_TRIPS_REQUEST, list),
  takeLatest(TripTypes.LIST_TRIPS_DETAIL_REQUEST, listDetail),
  takeLatest(TripTypes.GET_TRIPS_REQUEST, get),
  takeLatest(TripTypes.TRIPS_PDF_REQUEST, download),
  takeLatest(TripTypes.TRIPS_PAYMENT_REQUEST, payment),
  takeLatest(TripTypes.TRIPS_APROVAL_REQUEST, approval),
  takeLatest(TripTypes.TRIPS_APPROVAL_ALL_REQUEST, approvalAll),
  takeLatest(TripTypes.TRIP_REPORT_HTML_REQUEST, reportHtml),
  takeLatest(TripTypes.TRIP_REPORT_REQUEST, tripReport),
  takeLatest(TripTypes.TRIP_UPDATE_CONTROL_REQUEST, tripUpdateControl),
  takeLatest(TripTypes.TRIP_CONTROL_SETTINGS_REQUEST, tripControlSettingsGet),
  takeLatest(
    TripTypes.TRIP_CONTROL_SETTINGS_UPDATE_REQUEST,
    tripControlSettingsUpdate,
  ),
  takeLatest(TripTypes.MONITORING_SETTINGS_REQUEST, monitoringSettingsList),
  takeLatest(
    TripTypes.MONITORING_SETTINGS_UPDATE_REQUEST,
    monitoringSettingsUpdate,
  ),
  takeLatest(TripTypes.TRIP_ASK_MISSING_ENTRIES, tripAskMissingEntries),
];
